<template>
  <div class="onboarding-connect-pages">
    <!-- Left selection : steps -->
    <OnboardingSteps
      :current-step="'pages'"
      :current-network="currentNetwork"
      @network-selected="handleNetworkSelected"
    />

    <!-- Right section : pages connection -->
    <div class="onboarding-content">
      <!-- Title section -->
      <div class="onboarding-header">
        <img
          :src="currentNetworkIcon"
          :alt="currentNetworkName"
          class="onboarding-network-icon"
        />
        <div class="onboarding-header-content">
          <h1 class="onboarding-header-title">
            {{ $t('onboarding.connect_pages.connection_title', { networkName: currentNetworkName }) }}
          </h1>
          <p class="onboarding-header-subtitle">
            {{ $t('onboarding.connect_pages.connection_subtitle', { networkName: currentNetworkName }) }}
          </p>
        </div>
      </div>

      <!--- Features badges selected-->
      <div class="onboarding-features-badges" v-if="networkFeatures.length > 0">
        <span
          class="onboarding-feature-badge"
          v-for="feature in networkFeatures"
          :key="feature"
          :data-tooltip="$t(`onboarding.features.${feature}.description`)"
        >
          {{ $t(`onboarding.features.${feature}.title`) }}
        </span>
      </div>

      <!-- Quota warning message -->
      <div class="onboarding-quota-warning" v-if="hasReachedPageQuota">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div>
          <strong>{{ $t('onboarding.connect_pages.quota_warning_title', { current: totalSelectedPages, max: userPlanPageQuota }) }}</strong>
          <p v-html="$t('onboarding.connect_pages.quota_warning_message')"></p>
        </div>
      </div>

      <!-- Connected pages list -->
      <div class="onboarding-pages-list">
        <!--- Connection card-->
        <div
          class="onboarding-page-card onboarding-add-page-card"
          :class="{ 'disabled': hasReachedPageQuota }"
          @click="connectNewPage"
        >
          <div class="onboarding-add-icon">+</div>
          <div class="onboarding-add-text">
            <span v-if="!hasReachedPageQuota">{{ $t('onboarding.connect_pages.connect_new_page') }}</span>
            <span v-else>{{ $t('onboarding.connect_pages.quota_reached') }}</span>
          </div>
        </div>
        <!--- Connected pages cards-->
        <div
          class="onboarding-page-card"
          :class="{ 
            'selected': isPageSelected(page.id),
            'pre-existing': isPagePreExisting(page.id),
            'non-deselectable': isPagePreExisting(page.id) && isPageSelected(page.id)
          }"
          v-for="page in connectedNetworkPages"
          :key="page.id"
          @click="togglePageSelection(page.id)"
        >
          <!-- Pre-existing badge -->
          <div v-if="isPagePreExisting(page.id)" class="pre-existing-badge">
            {{ getPreExistingBadgeText(page) }}
          </div>
          
          <img
            v-if="page.avatar && !imageLoadErrors[page.id]"
            :src="page.avatar"
            :alt="page.name"
            class="onboarding-page-avatar"
            @error="handleImageError(page.id)"
          />
          <div
            v-else
            class="onboarding-page-avatar avatar-placeholder"
            role="img"
            :aria-label="page.name"
          >
            {{ page.name.charAt(0).toUpperCase() }}
          </div>
          <div class="onboarding-page-info">
            <div class="onboarding-page-name" :title="page.name" >{{ page.name }}</div>
            <div class="onboarding-page-username" :title="page.username">@{{ page.username }}</div>
          </div>
          <div class="onboarding-selection-indicator">
            <div 
              class="onboarding-selection-check"
              :class="{ 'checked': isPageSelected(page.id) }"
            ></div>
          </div>
        </div>
      </div>
      
      <!--- Footer with NAV buttons-->
      <div class="onboarding-footer-container">
        <div class="onboarding-pages-counter">
          <span v-if="selectedPageIds.length === 1">
              {{ selectedPageIds.length }} 
              {{ $t('onboarding.connect_pages.page_selected_singular') }}
            </span>
            <span v-else>
              {{ selectedPageIds.length }}
              {{ $t('onboarding.connect_pages.page_selected_plural') }}
            </span>
            <!-- <br />
            <span v-if="remainingPagesCount === 1">
              {{ remainingPagesCount }} 
              Page restante 
            </span>
            <span v-else>
              {{ remainingPagesCount }} 
              Pages restantes
            </span> -->
        </div>
        <div class="onboarding-footer">
        <!-- <button class="onboarding-btn-secondary" @click="skipStep">
          Passer
        </button> -->
        <button
          class="onboarding-btn-primary"
          @click="goToNextStep"
          :disabled="!canProceed"
        >
          {{ $t('onboarding.connect_pages.next_step') }}
          <!-- <span class="onboarding-step-indicator">
            {{ currentStepIndex }}/{{ totalNetworks }}
          </span> -->
        </button>
        </div>
      </div>
    </div>

    <!-- Loading overlay -->
    <LoadingSpinner :show="isConnecting" />

    <!-- Bluesky authentication modal -->
    <BlueskyModal
      :active="blueskyAuth.active"
      @connect="handleBlueskyConnect"
      @cancel="handleBlueskyCancel"
      ref="blueskyModal"
    />

    <!-- Google refresh token modal -->
    <GoogleRefreshModal
      :active="googleRefresh.active"
      @reconnect="handleGoogleReconnect"
      @skip="handleGoogleRefreshSkip"
    />
    
    <!-- Exit confirmation modal -->
    <modal-popup />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { EventBus } from '@/event-bus';
import moment from 'moment';
import Common from '../../mixins/Common.vue';
import Notif from '../../mixins/Notif.vue';
import Pages from '../../mixins/Pages.vue';
import Social from '../../mixins/Social.vue';
import User from '../../mixins/User.vue';
import OnboardingSteps from '../shared/OnboardingSteps.vue';
import LoadingSpinner from '../shared/LoadingSpinner.vue';
import BlueskyModal from '../shared/BlueskyModal.vue';
import GoogleRefreshModal from '../shared/GoogleRefreshModal.vue';
import modalPopup from '@/components/dash/modalPopup.vue';
import selectPage from '../../../store/modules/selectPage';
import Lock from '@/utils/Lock.js';
import { persistOnboardingPages, restoreOnboardingPages, clearOnboardingFlags } from '../utils/onboardingPersistence';
import { getNetworkName, getNetworkIcon } from '../utils/networkMaps';

export default {
  name: 'StepConnectPages',

  components: {
    OnboardingSteps,
    LoadingSpinner,
    BlueskyModal,
    GoogleRefreshModal,
    modalPopup
  },

  mixins: [Pages, Social, Common, Notif, User],

  data() {
    const savedIndex = localStorage.getItem('onboarding_current_index')
    const isOAuthReturn = localStorage.getItem('onboarding_mode') === 'true'
    return {
      currentNetworkIndex: isOAuthReturn && savedIndex !== null ? parseInt(savedIndex, 10) : 0,
      selectedPageIds: [],
      maxSelectedPages: 10,
      oauthCallbackProcessed: false, // Prevent duplicate OAuth callback processing
      isConnecting: false, // Loading state to prevent race conditions
      imageLoadErrors: {}, // Track failed avatar images
      blueskyAuth: {
        active: false
      },
      googleRefresh: {
        active: false
      },
      loadingMessage: '', // Message displayed during connection
      userPageQuota: 10, // Will be calculated based on user plan
      
      // OAuth provider registry for better maintainability
      oauthProviders: {
        pinterest: {
          type: 'oauth2',
          detectBy: 'currentNetwork', // Detect by restored currentNetwork from localStorage
          verifyMethod: 'postPinterestVerify',
          successMessage: 'onboarding.connect_pages.success_pinterest'
        },
        linkedin: {
          type: 'oauth2',
          detectBy: 'currentNetwork',
          verifyMethod: 'postLinkedinVerify',
          successMessage: 'onboarding.connect_pages.success_linkedin'
        },
        youtube: {
          type: 'oauth2',
          detectBy: 'currentNetwork',
          verifyMethod: 'postYoutubeVerify',
          successMessage: 'onboarding.connect_pages.success_youtube'
        },
        tiktok: {
          type: 'oauth2',
          detectBy: 'currentNetwork',
          verifyMethod: 'postTiktokVerify',
          requiresState: true,
          successMessage: 'onboarding.connect_pages.success_tiktok'
        },
        twitter: {
          type: 'oauth2',
          detectBy: 'currentNetwork', // Twitter OAuth 2.0 (version 2)
          verifyMethod: 'newPage', // Directly call newPage with code
          usesNewPageDirectly: true, // Special flag to indicate newPage() is the verify method
          successMessage: 'onboarding.connect_pages.success_twitter'
        },
        threads: {
          type: 'oauth2',
          detectBy: 'state', // Detect by state parameter
          stateValue: 'threads-connect',
          verifyMethod: 'postThreadsVerify',
          successMessage: 'onboarding.connect_pages.success_threads'
        },
        instagram: {
          type: 'oauth2',
          detectBy: 'state',
          stateValue: 'ig_business-connect',
          verifyMethod: 'postInstagramBusinessVerify',
          successMessage: 'onboarding.connect_pages.success_instagram_business'
        },
        x: {
          type: 'oauth1',
          verifyMethod: 'addTwitterV1',
          successMessage: 'onboarding.connect_pages.success_x'
        }
      }
    }
  },

  computed: {
    ...mapState('onboarding', [
      'selectedNetworks',
      'selectedFeatures',
      'connectedPages',
      'selectedPageIds',
      'preExistingPageIds'
    ]),
    ...mapGetters('onboarding', ['totalConnectedPages']),

    currentNetwork() {
      return this.selectedNetworks[this.currentNetworkIndex] || null
    },

    currentNetworkName() {
      return getNetworkName(this.currentNetwork)
    },

    currentNetworkIcon() {
      return getNetworkIcon(this.currentNetwork)
    },

    connectedNetworkPages() {
      return this.connectedPages[this.currentNetwork] || []
    },

    // Features to display as badges for the current network
    // IMPORTANT: Only show UNIVERSAL features here (OAuth connection)
    // Network-specific features (native connection) are shown in StepConnectNatives
    networkFeatures() {
      // Universal features available via OAuth connection
      const universalFeatures = ['planning', 'reporting', 'moderation']
      
      // Networks that do NOT support certain features
      const unsupportedFeatures = {
        threads: ['moderation', 'reporting'],
        twitter: ['moderation']
      }
      
      const excluded = unsupportedFeatures[this.currentNetwork] || []
      
      // Filter selected features to only show universal ones supported by this network
      return this.selectedFeatures.filter(f => universalFeatures.includes(f) && !excluded.includes(f))
    },

    currentStepIndex() {
      return this.currentNetworkIndex + 1
    },

    totalNetworks() {
      return this.selectedNetworks.length
    },

    canProceed() {
      return true
    },

    remainingPagesCount() {
      return this.maxSelectedPages - this.selectedPageIds.length
    },

    // Calculate user's page quota based on their plan
    userPlanPageQuota() {
      // Lock.rs_limit defines social accounts limit per plan (name is misleading — not RSS only)
      // Plan tiers: 0 = Free, 1 = Starter, 2 = Standard, 3 = Premium
      const plan = this.getUserPlan || 0 // Default to free plan if no user
      const quotas = Lock.rs_limit // [1, 10, 60, 250] — social accounts limit
      
      console.log('[StepConnectPages] User plan:', plan)
      console.log('[StepConnectPages] Page quota for plan:', quotas[plan])
      
      return quotas[plan] || 10 // Default to 10 if plan not found
    },

    // Total pages across ALL networks (all connected, selected or not)
    totalPagesAcrossAllNetworks() {
      return Object.values(this.connectedPages).reduce((sum, pages) => sum + pages.length, 0)
    },

    // Total SELECTED pages across ALL networks (for quota display)
    // Matches OnboardingSteps.vue logic: counts only pages that exist in connectedPages
    // AND are selected/pre-existing (prevents counting failed connections)
    totalSelectedPages() {
      let count = 0
      Object.keys(this.connectedPages).forEach(network => {
        const pages = this.connectedPages[network] || []
        const selectedIds = this.selectedPageIds[network] || []
        const preExistingIds = this.preExistingPageIds[network] || []
        pages.forEach(page => {
          const isSelected = selectedIds.includes(page.id) ||
            (page.network_id && selectedIds.includes(page.network_id))
          const isPreExisting = preExistingIds.includes(page.id) ||
            (page.network_id && preExistingIds.includes(page.network_id))
          if (isSelected || isPreExisting) {
            count++
          }
        })
      })
      return count
    },

    // Check if user has reached their page quota (based on SELECTED pages)
    hasReachedPageQuota() {
      return this.totalSelectedPages >= this.userPlanPageQuota
    },

    // Features that require a native connection
    nativeFeaturesForNetwork() {
      if (!this.currentNetwork) return []
      
      // Check if user selected native connection for this network (new unified system)
      const hasNative = this.selectedFeatures.some(f => 
        f === `${this.currentNetwork}.native`
      )
      
      return hasNative ? [`${this.currentNetwork}.native`] : []
    },

    requiresNativeConnection() {
      // Check if user selected native features
      if (this.nativeFeaturesForNetwork.length === 0) {
        return false
      }
      
      // Check if any selected pages DON'T have native connection yet
      const selectedPages = this.connectedNetworkPages.filter(p => 
        this.selectedPageIds.includes(p.id)
      )
      
      const pagesNeedingNative = selectedPages.filter(page => {
        const hasNativeConnection = page.story_config && page.story_config.created === true
        return !hasNativeConnection // Only pages without native connection
      })
      
      console.log('[StepConnectPages] requiresNativeConnection check:', {
        network: this.currentNetwork,
        nativeFeatures: this.nativeFeaturesForNetwork.length,
        selectedPages: selectedPages.length,
        pagesNeedingNative: pagesNeedingNative.length
      })
      
      return pagesNeedingNative.length > 0
    },

    requiresExtension() {
      // LinkedIn, TikTok and Facebook require web extension
      const extensionNetworks = ['linkedin', 'tiktok', 'facebook']
      
      // Check if network requires extension
      if (!extensionNetworks.includes(this.currentNetwork)) {
        return false
      }
      
      // Check if user selected native features
      if (this.nativeFeaturesForNetwork.length === 0) {
        return false
      }
      
      // Check if any selected pages DON'T have native connection yet
      const selectedPages = this.connectedNetworkPages.filter(p => 
        this.selectedPageIds.includes(p.id)
      )
      
      const pagesNeedingExtension = selectedPages.filter(page => {
        const hasNativeConnection = page.story_config && page.story_config.created === true
        return !hasNativeConnection // Only pages without native connection
      })
      
      console.log('[StepConnectPages] requiresExtension check:', {
        network: this.currentNetwork,
        nativeFeatures: this.nativeFeaturesForNetwork.length,
        selectedPages: selectedPages.length,
        pagesNeedingExtension: pagesNeedingExtension.length
      })
      
      return pagesNeedingExtension.length > 0
    }
  },

  watch: {
    // Automatically sync selectedPageIds when user switches networks
    currentNetwork: {
      immediate: false, // Don't run on mount (handled by loadSelectedPages in mounted())
      handler(newNetwork) {
        console.log('[StepConnectPages] Network changed to:', newNetwork)
        this.loadSelectedPages()
      }
    },

    // Persist current network index so plain page reloads return to the right network
    currentNetworkIndex(val) {
      localStorage.setItem('onboarding_current_index', val.toString())
    },

    // Deferred initialization for plain page reloads.
    // Child mounts before parent (Onboarding.vue), so selectedNetworks is empty when mounted()
    // fires. This watcher triggers once the parent has restored Vuex from localStorage.
    selectedNetworks: {
      handler(networks) {
        if (!this._deferredInit || networks.length === 0) return
        this._deferredInit = false
        console.log('[StepConnectPages] selectedNetworks populated after reload - running deferred init')

        // Restore network index (saved by currentNetworkIndex watcher above)
        const savedIndex = localStorage.getItem('onboarding_current_index')
        if (savedIndex !== null) {
          const parsed = Number.parseInt(savedIndex)
          if (!Number.isNaN(parsed) && parsed > 0 && parsed < networks.length) {
            console.log('[StepConnectPages] Restoring network index:', parsed, '→', networks[parsed])
            this.currentNetworkIndex = parsed
          }
        }

        this.loadExistingPages().then(() => this.loadSelectedPages())
      }
    }
  },

  methods: {
    connectNewPage() {
      if (this.isConnecting) return
      
      // Check if user has reached page quota
      if (this.hasReachedPageQuota) {
        this.notify({
          time: 5000,
          title: this.$t('onboarding.notifications.quota_reached_title'),
          text: this.$t('onboarding.notifications.quota_reached_message', { max: this.userPlanPageQuota }),
          color: 'warning'
        })
        return
      }
      
      localStorage.setItem('onboarding_mode', 'true')
      localStorage.setItem('onboarding_current_network', this.currentNetwork)
      localStorage.setItem('onboarding_current_index', this.currentNetworkIndex.toString())
      localStorage.setItem('onboarding_selected_networks', JSON.stringify(this.selectedNetworks))
      localStorage.setItem('onboarding_selected_features', JSON.stringify(this.selectedFeatures))
      
      // Use persistOnboardingState() for comprehensive state persistence
      this.persistOnboardingState()
      
      console.log('[StepConnectPages] localStorage flags set:', {
        network: this.currentNetwork,
        index: this.currentNetworkIndex
      })
      
      switch(this.currentNetwork) {
        case 'facebook':
          this.connectFB()
          break
        case 'instagram':
          this.connectInsta()
          break
        case 'linkedin':
          this.connectLinkedin({ refresh: false })
          break
        case 'twitter':
        case 'x':
          this.connectTwitter({ refresh: false, version: 2, page: null })
          break
        case 'google':
          this.loginWithGoogle()
          break
        case 'threads':
          this.connectThreads()
          break
        case 'tiktok':
          this.connectTiktok({ refresh: false })
          break
        case 'youtube':
          this.connectYoutube()
          break
        case 'bluesky':
          this.loginWithBluesky()
          break
        case 'pinterest':
          this.connectPinterest()
          break
        default:
          console.warn('[StepConnectPages] Network not supported:', this.currentNetwork)
      }
    },

    // Facebook connection method
    connectFB() {
      this.$ga.event('onboarding', 'connect-facebook')
      this.isConnecting = true
      
      // eslint-disable-next-line
      FB.login(
        response => {
          if (response.status === 'connected') {
            const accessToken = response.authResponse.accessToken

            // Validate token (same as dashboard)
            this.getUserInfoFb(accessToken).then(res => {
              if (!res.ok) {
                console.error('[StepConnectPages] getUserInfoFb failed:', res)
                this.isConnecting = false
                return
              }

              // Exchange for long-lived token (same as dashboard)
              this.getLongLiveToken(accessToken).then(res => {
                if (!res.ok) {
                  console.error('[StepConnectPages] getLongLiveToken failed:', res)
                  this.isConnecting = false
                  return
                }

                const longLivedToken = res.body.access_token

                // Retrieve pages with pagination (same as dashboard)
                this.retrievePages(longLivedToken).then(async res => {
                  if (!res.ok || !res.body || !res.body.data) {
                    console.error('[StepConnectPages] retrievePages failed:', res)
                    this.isConnecting = false
                    return
                  }

                  // Collect all pages with pagination (same as dashboard)
                  let allPagesData = [...res.body.data]
                  let nextPagesEndpoint = res.body.paging && res.body.paging.next ? res.body.paging.next : null

                  while (nextPagesEndpoint) {
                    try {
                      const nextPages = await this.$http.get(nextPagesEndpoint)
                      nextPagesEndpoint = nextPages.body.paging && nextPages.body.paging.next ? nextPages.body.paging.next : null
                      allPagesData = allPagesData.concat(nextPages.body.data)
                    } catch (e) {
                      console.error('[StepConnectPages] Pagination error:', e)
                      nextPagesEndpoint = null
                    }
                  }

                  if (allPagesData.length > 0) {
                    const promises = allPagesData.map(page => {
                      const pageData = {
                        network: 'facebook',
                        network_id: page.id,
                        name: page.name,
                        avatar: page.picture?.data?.url || null,
                        token: page.access_token
                      }

                      // Append city to name for multi-location pages (same as dashboard)
                      if (page.parent_page && page.parent_page.id && page.location && page.location.city) {
                        pageData.name += ` (${page.location.city})`
                      }
                      
                      return this.newPage(pageData).then((res) => {
                        const hasValidUuid = res.ok && res.body?.success && res.body?.page?.id
                        // 422 or 409 = page already exists in backend, still a valid connection
                        const isDuplicate = res.status === 422 || res.status === 409
                        
                        if (!hasValidUuid && !isDuplicate) {
                          console.warn('[StepConnectPages] Backend returned invalid response for Facebook page:', pageData.name)
                          console.error('[StepConnectPages] Response:', res)
                        }
                        
                        const pageToAdd = {
                          id: hasValidUuid ? res.body.page.id : page.id,
                          network_id: page.id,
                          name: pageData.name,
                          username: page.id,
                          avatar: page.picture?.data?.url || null,
                          hasUuid: hasValidUuid
                        }
                        
                        this.addPageIfNotExists('facebook', pageToAdd)
                        
                        // Count as success if backend created page OR page already existed (422/409)
                        return (hasValidUuid || isDuplicate) ? (hasValidUuid ? res.body.page.id : page.id) : null
                      }).catch(error => {
                        console.error('[StepConnectPages] newPage() error for Facebook:', pageData.name, error)
                        
                        const pageToAdd = {
                          id: page.id,
                          network_id: page.id,
                          name: pageData.name,
                          username: page.id,
                          avatar: page.picture?.data?.url || null,
                          hasUuid: false
                        }
                        
                        this.addPageIfNotExists('facebook', pageToAdd)
                        
                        return null
                      })
                    })
                    
                    Promise.all(promises).then((results) => {
                      const successCount = results.filter(r => r !== null).length
                      // Fallback: pages may already be in the store (pre-loaded by loadExistingPages
                      // or added via the catch handler), even if all newPage() calls returned null.
                      // In that case treat it as a successful re-connection instead of an error.
                      const pagesInStore = (this.connectedPages['facebook'] || []).length > 0

                      this.$nextTick(() => {
                        this.$forceUpdate()
                      })
                      
                      if (successCount > 0 || pagesInStore) {
                        this.saveSelectedPages()
                        this.persistOnboardingState() // CRITICAL: Persist to localStorage after Facebook connection
                        this.finalizeConnection(this.$t('onboarding.connect_pages.success_facebook'))
                      } else {
                        console.error('[StepConnectPages] All Facebook newPage() calls failed')
                        this.notify({
                          time: 5000,
                          title: this.$t('onboarding.notifications.error_title'),
                          text: this.$t('onboarding.connect_pages.error_connection_failed', { provider: 'Facebook' }),
                          color: 'danger'
                        })
                        this.isConnecting = false
                      }
                    }).catch(error => {
                      console.error('[StepConnectPages] Error in Facebook Promise.all:', error)
                      this.isConnecting = false
                    })
                  } else {
                    this.isConnecting = false
                  }
                })
              })
            })
          } else {
            this.isConnecting = false
          }
        },
        {
          scope: 'public_profile, pages_show_list, read_insights, pages_manage_posts, pages_manage_engagement, pages_read_user_content, pages_manage_metadata, pages_messaging, business_management, instagram_basic, instagram_content_publish, instagram_manage_insights, instagram_manage_comments, instagram_manage_messages',
          return_scopes: true,
          auth_type: 'rerequest'
        }
      )
    },

    // Instagram connection method (aligned with dashboard flow: long-lived token + pagination + backend APIs)
    connectInsta() {
      this.$ga.event('onboarding', 'connect-instagram')
      this.isConnecting = true
      
      // eslint-disable-next-line
      FB.login(
        response => {
          console.log('[Instagram OAuth] FB.login response:', response)
          
          if (response.status === 'connected') {
            const accessToken = response.authResponse.accessToken

            // Validate token (same as dashboard)
            this.getUserInfoFb(accessToken).then(res => {
              if (!res.ok) {
                console.error('[StepConnectPages] getUserInfoFb failed:', res)
                this.isConnecting = false
                return
              }

              // Exchange for long-lived token (same as dashboard)
              this.getLongLiveToken(accessToken).then(res => {
                if (!res.ok) {
                  console.error('[StepConnectPages] getLongLiveToken failed:', res)
                  this.isConnecting = false
                  return
                }

                const longLivedToken = res.body.access_token

                // Retrieve pages with pagination (same as dashboard)
                this.retrievePages(longLivedToken).then(async res => {
                  if (!res.ok || !res.body || !res.body.data) {
                    console.error('[StepConnectPages] retrievePages failed:', res)
                    this.isConnecting = false
                    return
                  }

                  // Collect all Facebook pages with pagination (same as dashboard)
                  let allPagesFB = [...res.body.data]
                  let nextPagesEndpoint = res.body.paging && res.body.paging.next ? res.body.paging.next : null

                  while (nextPagesEndpoint) {
                    try {
                      const nextPages = await this.$http.get(nextPagesEndpoint)
                      nextPagesEndpoint = nextPages.body.paging && nextPages.body.paging.next ? nextPages.body.paging.next : null
                      allPagesFB = allPagesFB.concat(nextPages.body.data)
                    } catch (e) {
                      console.error('[StepConnectPages] Instagram pagination error:', e)
                      nextPagesEndpoint = null
                    }
                  }

                  console.log('[Instagram OAuth] Total FB pages after pagination:', allPagesFB.length)

                  // Fetch Instagram business accounts for each FB page (same as dashboard using retrieveInstaPages + getUserInfoInsta)
                  const promises = allPagesFB.map(async page => {
                    try {
                      const resInstaPages = await this.retrieveInstaPages(page.id, longLivedToken)
                      if (!resInstaPages.body.instagram_business_account) return null

                      const resInfoInsta = await this.getUserInfoInsta(resInstaPages.body.instagram_business_account.id, longLivedToken)

                      const pageData = {
                        network: 'instagram',
                        network_id: resInfoInsta.body.id,
                        name: resInfoInsta.body.username,
                        avatar: resInfoInsta.body.profile_picture_url || null,
                        token: longLivedToken,
                        affected_to: page.id
                      }

                      const pageResponse = await this.newPage(pageData)

                      // Case 1: newPage succeeded — use response directly (no extra API call)
                      if (pageResponse.ok && pageResponse.body?.success && pageResponse.body?.page?.id) {
                        const createdPage = pageResponse.body.page
                        console.log('[Instagram OAuth] newPage succeeded, using response directly:', createdPage.id)
                        this.addPageIfNotExists('instagram', {
                          id: createdPage.id,
                          network_id: createdPage.network_id || resInfoInsta.body.id,
                          name: resInfoInsta.body.username,
                          username: resInfoInsta.body.username,
                          avatar: resInfoInsta.body.profile_picture_url || null,
                          hasUuid: true
                        })
                        return createdPage.id
                      }

                      // Case 2: newPage failed (e.g., 422 page already exists) — fallback to getPages()
                      console.warn('[Instagram OAuth] newPage did not return valid page (status:', pageResponse.status, '), falling back to getPages()')
                      const pagesRes = await this.getPages()
                      if (!pagesRes || !pagesRes.ok || !pagesRes.body) {
                        console.error('[Instagram OAuth] getPages() fallback also failed')
                        return null
                      }
                      const allPages = pagesRes.body.pages || []
                      const instagramPages = allPages.filter(p => p.network === 'instagram')
                      const existingPage = instagramPages.find(p => p.network_id === resInfoInsta.body.id)
                        || instagramPages.find(p => p.name === resInfoInsta.body.username || p.username === resInfoInsta.body.username)

                      if (existingPage && existingPage.id) {
                        console.log('[Instagram OAuth] Found existing page via getPages() fallback:', existingPage.id)
                        this.addPageIfNotExists('instagram', {
                          id: existingPage.id,
                          network_id: existingPage.network_id || resInfoInsta.body.id,
                          name: resInfoInsta.body.username,
                          username: resInfoInsta.body.username,
                          avatar: resInfoInsta.body.profile_picture_url || null,
                          hasUuid: true
                        })
                        return existingPage.id
                      }

                      console.error('[Instagram OAuth] Could not find Instagram page via newPage or getPages:', resInfoInsta.body.username)
                      return null
                    } catch (error) {
                      console.error('[StepConnectPages] Error processing Instagram for FB page:', page.id, error)
                      return null
                    }
                  })

                  Promise.all(promises).then((results) => {
                    const successCount = results.filter(r => r !== null).length

                    this.$nextTick(() => {
                      this.$forceUpdate()
                    })

                    if (successCount > 0) {
                      this.saveSelectedPages()
                      this.persistOnboardingState() // CRITICAL: Persist to localStorage after Instagram connection
                      this.finalizeConnection(this.$t('onboarding.connect_pages.success_instagram'))
                    } else {
                      this.notify({
                        time: 5000,
                        title: this.$t('onboarding.notifications.error_title'),
                        text: this.$t('onboarding.connect_pages.error_instagram_connection'),
                        color: 'danger'
                      })
                      this.isConnecting = false
                    }
                  }).catch(error => {
                    console.error('[StepConnectPages] Error in Instagram Promise.all:', error)
                    this.isConnecting = false
                  })
                })
              })
            })
          } else {
            this.notify({
              time: 5000,
              title: this.$t('onboarding.notifications.error_title'),
              text: this.$t('onboarding.connect_pages.error_instagram_cancelled'),
              color: 'warning'
            })
            this.isConnecting = false
          }
        },
        {
          scope: 'public_profile, pages_show_list, pages_read_engagement, pages_manage_metadata, read_insights, pages_manage_posts, pages_manage_engagement, pages_read_user_content, business_management, instagram_basic, instagram_content_publish, instagram_manage_insights, instagram_manage_comments, instagram_manage_messages',
          return_scopes: true,
          auth_type: 'rerequest'
        }
      )
    },

    // Google connection method
    loginWithGoogle() {
      this.$ga.event('onboarding', 'connect-google')
      this.isConnecting = true
      
      this.$gAuth.getAuthCode()
      .then(async authCode => {
        let res = await this.getTokensGoogle(authCode)
        
        if(res && res.body && !res.body.success) {
          this.notify({
            time: 5000,
            title: this.$t('onboarding.notifications.error_title'),
            text: this.$t('onboarding.connect_pages.error_google_connection'),
            color: 'danger'
          })
          this.isConnecting = false
          return
        }
        
        if(res.body.pages && res.body.pages.length > 0) {
          const savePromises = res.body.pages.map((page) => {
            // Build page data with tokens (same as Pages.vue connectGoogle method)
            const pageData = {
              network: 'google',
              network_id: page.network_id,
              name: page.name,
              avatar: page.avatar
            }
            
            // Add OAuth tokens from Google API response
            if (page.access_token) {
              pageData.token = page.access_token
            }
            
            if (page.refresh_token) {
              pageData.refresh_token = page.refresh_token
            }
            
            if (page.expires_at) {
              // Convert expires_at (in seconds) to ISO date format (same as Pages.vue)
              pageData.expires_at = moment().add(page.expires_at, 'seconds').format()
            }
            
            console.log('[StepConnectPages] Google pageData with tokens:', pageData)
            
            return this.newPage(pageData).then(response => {
              console.log('[StepConnectPages] Google newPage response:', response)
              // Check if response is successful - backend returns { success: true, page: {...} }
              const hasValidUuid = response && response.ok && response.body && response.body.success && response.body.page && response.body.page.id
              
              if (hasValidUuid) {
                return { originalPage: page, createdPage: response.body.page, hasUuid: true }
              } else {
                console.warn('[StepConnectPages] Backend returned invalid response for Google page:', page.name)
                console.error('[StepConnectPages] Response details:', {
                  ok: response && response.ok,
                  status: response && response.status,
                  success: response && response.body && response.body.success,
                  hasPage: response && response.body && !!response.body.page
                })
                // Return fallback with network_id as id
                return { originalPage: page, createdPage: { id: page.network_id, network_id: page.network_id, name: page.name, avatar: page.avatar }, hasUuid: false }
              }
            }).catch(err => {
              console.error('[StepConnectPages] Error saving Google page:', page.name, err)
              // Return fallback with network_id as id
              return { originalPage: page, createdPage: { id: page.network_id, network_id: page.network_id, name: page.name, avatar: page.avatar }, hasUuid: false }
            })
          })
          
          Promise.all(savePromises).then((results) => {
            // Filter successful results (not null) and count them
            const successfulResults = results.filter(r => r !== null)
            const successCount = successfulResults.length
            
            console.log('[StepConnectPages] Google save results:', successCount, 'successful out of', results.length)
            
            if (successCount > 0) {
              successfulResults.forEach(({ originalPage, createdPage, hasUuid }) => {
                this.addPageIfNotExists('google', {
                  id: createdPage.id,
                  network_id: originalPage.network_id,
                  name: originalPage.name,
                  username: originalPage.network_id,
                  avatar: originalPage.avatar,
                  hasUuid: hasUuid
                })
              })
              
              this.$nextTick(() => {
                this.$forceUpdate()
              })
              
              // Save and persist state
              this.saveSelectedPages()
              this.persistOnboardingState()
              
              // Check if any page is missing refresh_token (Google doesn't always provide it)
              const missingRefreshToken = successfulResults.some(({ originalPage }) => !originalPage.refresh_token)
              
              if (missingRefreshToken) {
                console.log('[StepConnectPages] Google pages missing refresh_token — showing refresh modal')
                this.isConnecting = false
                this.googleRefresh.active = true
              } else {
                this.finalizeConnection(this.$t('onboarding.connect_pages.success_google'))
              }
            } else {
              console.error('[StepConnectPages] All Google pages failed to save')
              this.notify({
                time: 5000,
                title: this.$t('onboarding.notifications.error_title'),
                text: this.$t('onboarding.connect_pages.error_google_pages_failed'),
                color: 'danger'
              })
              this.isConnecting = false
            }
          }).catch(error => {
            console.error('[StepConnectPages] Error in Google Promise.all:', error)
            this.notify({
              time: 5000,
              title: this.$t('onboarding.notifications.error_title'),
              text: this.$t('onboarding.connect_pages.error_google_connection'),
              color: 'danger'
            })
            this.isConnecting = false
          })
        } else {
          console.log('[StepConnectPages] No Google pages found')
          this.notify({
            time: 5000,
            title: this.$t('onboarding.notifications.info_title'),
            text: this.$t('onboarding.connect_pages.info_no_google_pages'),
            color: 'warning'
          })
          this.isConnecting = false
        }
      }).catch(error => {
        console.error('[StepConnectPages] Google OAuth error:', error)
        this.notify({
          time: 5000,
          title: this.$t('onboarding.notifications.error_title'),
          text: error.message || this.$t('onboarding.connect_pages.error_google_oauth'),
          color: 'danger'
        })
        this.isConnecting = false
      })
    },

    // Bluesky connection method - requires credentials (handle + app password)
    loginWithBluesky() {
      this.$ga.event('onboarding', 'connect-bluesky')
      
      // Open Bluesky auth modal
      this.blueskyAuth.active = true
    },

    // Handle Bluesky connection from modal
    async handleBlueskyConnect({ handle, password }) {
      await this.connectBlueskyWithCredentials(handle, password)
    },

    // Handle Bluesky modal cancel
    handleBlueskyCancel() {
      this.blueskyAuth.active = false
    },

    // Handle Google refresh modal: user chose to reconnect after revoking app access
    handleGoogleReconnect() {
      this.googleRefresh.active = false
      this.loginWithGoogle()
    },

    // Handle Google refresh modal: user chose to skip (token works ~1h, can refresh later from dashboard)
    handleGoogleRefreshSkip() {
      this.googleRefresh.active = false
      this.finalizeConnection(this.$t('onboarding.connect_pages.success_google'))
    },

    // Connect Bluesky with credentials
    async connectBlueskyWithCredentials(identifier, password) {
      this.isConnecting = true
      this.blueskyAuth.active = false
      
      try {
        const res = await this.postLoginBluesky({
          identifier: identifier,
          password: password
        })
        
        
        if (res && res.body && res.body.accessJwt && res.body.refreshJwt && res.body.handle) {
          
          const page = {
            network: 'bluesky',
            token: res.body.accessJwt,
            refresh_token: res.body.refreshJwt,
            name: res.body.handle,
            avatar: null,
            network_id: res.body.did
          }
          
          // Fetch profile to get avatar
          try {
            const profile = await this.getBlueskyProfile(res.body.handle, res.body.accessJwt)
            
            if (profile && profile.body && profile.body.avatar) {
              page.avatar = profile.body.avatar
            }
          } catch (profileErr) {
            console.warn('[StepConnectPages] Could not fetch Bluesky profile, continuing without avatar:', profileErr)
          }
          
          // Save page to backend
          const pageResponse = await this.newPage(page)
          console.log('[StepConnectPages] Bluesky newPage response:', pageResponse)
          
          // Check if response is successful - backend returns { success: true, page: {...} }
          const hasValidUuid = pageResponse && pageResponse.ok && pageResponse.body && pageResponse.body.success && pageResponse.body.page && pageResponse.body.page.id
          
          if (hasValidUuid) {
            const createdPage = pageResponse.body.page
            
            // Add to store
            this.addPageIfNotExists('bluesky', {
              id: createdPage.id,
              network_id: page.network_id,
              name: page.name,
              username: page.name,
              avatar: page.avatar,
              hasUuid: true
            })
            
            this.$nextTick(() => {
              this.$forceUpdate()
            })
            
            // Finalize with 2s delay
            this.saveSelectedPages()
            this.persistOnboardingState() // CRITICAL: Persist to localStorage after Bluesky connection
            this.finalizeConnection(this.$t('onboarding.connect_pages.success_bluesky'))
          } else {
            console.warn('[StepConnectPages] Backend returned invalid response for Bluesky, using fallback')
            console.error('[StepConnectPages] Response details:', {
              ok: pageResponse && pageResponse.ok,
              status: pageResponse && pageResponse.status,
              success: pageResponse && pageResponse.body && pageResponse.body.success,
              hasPage: pageResponse && pageResponse.body && !!pageResponse.body.page
            })
            
            // Fallback: use network_id as id
            this.addPageIfNotExists('bluesky', {
              id: page.network_id,
              network_id: page.network_id,
              name: page.name,
              username: page.name,
              avatar: page.avatar,
              hasUuid: false
            })
            
            this.$nextTick(() => {
              this.$forceUpdate()
            })
            
            this.saveSelectedPages()
            this.persistOnboardingState() // CRITICAL: Persist to localStorage after Bluesky connection (fallback)
            this.finalizeConnection(this.$t('onboarding.connect_pages.success_bluesky'))
          }
        } else {
          console.error('[StepConnectPages] Invalid Bluesky login response:', res)
          this.notify({
            time: 5000,
            title: this.$t('onboarding.notifications.error_title'),
            text: this.$t('onboarding.connect_pages.error_bluesky_invalid_response'),
            color: 'danger'
          })
          this.isConnecting = false
        }
      } catch (error) {
        console.error('[StepConnectPages] Bluesky connection error:', error)
        
        // Reset modal loading state
        if (this.$refs.blueskyModal) {
          this.$refs.blueskyModal.resetLoading()
        }
        
        // Reopen modal with error
        this.blueskyAuth.active = true
        
        this.notify({
          time: 8000,
          title: this.$t('onboarding.notifications.connection_error_title'),
          text: error.body?.message || this.$t('onboarding.connect_pages.error_bluesky_connection'),
          color: 'danger'
        })
        this.isConnecting = false
      }
    },

    togglePageSelection(pageId) {
      console.log('[StepConnectPages] togglePageSelection called with:', pageId)
      console.log('[StepConnectPages] Current selectedPageIds:', [...this.selectedPageIds])
      console.log('[StepConnectPages] Current network:', this.currentNetwork)
      console.log('[StepConnectPages] connectedNetworkPages:', this.connectedNetworkPages.map(p => ({ id: p.id, name: p.name })))
      
      // Check if page is pre-existing (cannot be deselected)
      const isPreExisting = this.isPagePreExisting(pageId)
      
      const index = this.selectedPageIds.indexOf(pageId)
      console.log('[StepConnectPages] pageId index in array:', index)
      console.log('[StepConnectPages] Is pre-existing:', isPreExisting)
      
      if (index !== -1) {
        // Prevent deselection of pre-existing pages
        if (isPreExisting) {
          console.log('[StepConnectPages] Cannot deselect pre-existing page')
          this.notify({
            time: 3000,
            title: this.$t('onboarding.notifications.info_title', 'Information'),
            text: this.$t('onboarding.connect_pages.info_cannot_deselect_preexisting', 'Cette page était déjà connectée et ne peut pas être désélectionnée'),
            color: 'primary'
          })
          return
        }
        
        this.selectedPageIds.splice(index, 1)
        console.log('[StepConnectPages] Page deselected, new array:', [...this.selectedPageIds])
      } else {
        if (this.selectedPageIds.length < this.maxSelectedPages) {
          this.selectedPageIds.push(pageId)
          console.log('[StepConnectPages] Page selected, new array:', [...this.selectedPageIds])
        } else {
          console.warn('[StepConnectPages] Cannot select - max pages reached:', this.maxSelectedPages)
        }
      }
      
      this.saveSelectedPages()
    },

    isPageSelected(pageId) {
      return this.selectedPageIds.includes(pageId)
    },

    isPagePreExisting(pageId) {
      return this.$store.getters['onboarding/isPagePreExisting'](this.currentNetwork, pageId)
    },

    getPreExistingBadgeText(page) {
      // Check if page has native connection (story_config.created = true)
      const hasNativeConnection = page.story_config && page.story_config.created === true
      
      if (hasNativeConnection) {
        return this.$t('onboarding.connect_pages.badge_previously_connected_native')
      }
      return this.$t('onboarding.connect_pages.badge_previously_connected')
    },

    saveSelectedPages() {
      console.log('[StepConnectPages] Store updated - selectedPageIds for', this.currentNetwork, ':', [...this.selectedPageIds])
      this.$store.commit('onboarding/SET_SELECTED_PAGE_IDS', {
        network: this.currentNetwork,
        pageIds: [...this.selectedPageIds]
      })
    },

    /**
     * Persist complete onboarding state to localStorage
     * CRITICAL: This must be called after EVERY page connection to prevent data loss on refresh
     */
    persistOnboardingState() {
      persistOnboardingPages(this.$store)
      console.log('[StepConnectPages] Onboarding state persisted to localStorage')
    },

    /**
     * Finalize connection with loading delay to prevent race conditions
     * @param {string} successMessage - Message to display on success (can be i18n key or fallback string)
     */
    finalizeConnection(successMessage) {
      // Translate message with fallback to handle missing i18n keys in sandbox
      let translatedMessage = successMessage
      
      // If successMessage looks like an i18n key (contains dots), try to translate it
      if (successMessage && successMessage.includes('.')) {
        const translated = this.$t(successMessage)
        // If translation failed (returns the key itself), use generic fallback
        translatedMessage = translated !== successMessage ? translated : this.$t('onboarding.connect_pages.success_generic', 'Page connectée avec succès')
      }
      
      this.notify({
        time: 5000,
        title: this.$t('onboarding.notifications.success_title', 'Succès'),
        text: translatedMessage,
        color: 'success'
      })
      
      // Stop loading spinner after delay to ensure UI updates
      setTimeout(() => {
        this.isConnecting = false
      }, 2000)
    },

    handleImageError(id) {
      this.imageLoadErrors = { ...this.imageLoadErrors, [id]: true }
    },

    /**
     * Add page to store only if it doesn't already exist (check by network_id, id, or name)
     * @param {string} network - Network name (facebook, instagram, etc.)
     * @param {Object} page - Page object with id, network_id, name, etc.
     * @returns {boolean} - true if page was added, false if it already existed
     */
    addPageIfNotExists(network, page) {
      // IMPORTANT: Use this.connectedPages[network] instead of this.connectedNetworkPages
      // to ensure we're checking the correct network, not the currently displayed one
      const existingPages = this.connectedPages[network] || []
      
      // Check if page already exists by:
      // 1. network_id (platform-specific ID like Facebook page ID)
      // 2. id (our backend UUID)
      // 3. name + network (as fallback for cases where IDs differ but it's the same page)
      const pageExists = existingPages.some(p => {
        // Check by network_id (strongest match - platform-specific ID)
        if (page.network_id && p.network_id === page.network_id) {
          return true
        }
        
        // Check by id (backend UUID)
        if (page.id && p.id === page.id) {
          return true
        }
        
        // Check by name as fallback (same page name on same network)
        // This prevents duplicates when backend returns different UUIDs for same page
        if (page.name && p.name === page.name) {
          console.log(`[StepConnectPages] Found duplicate by name: ${page.name}`)
          return true
        }
        
        return false
      })
      
      if (pageExists) {
        console.log(`[StepConnectPages] Page ${page.name} (${page.network_id}) already exists in ${network}, skipping duplicate`)
        
        // Find the existing page to get its ID
        const existingPage = existingPages.find(p => 
          (page.network_id && p.network_id === page.network_id) || 
          (page.id && p.id === page.id) ||
          (page.name && p.name === page.name)
        )
        
        // Auto-select if not already selected and under limit (only for current network)
        if (existingPage && network === this.currentNetwork && !this.selectedPageIds.includes(existingPage.id)) {
          if (this.selectedPageIds.length < this.maxSelectedPages) {
            this.selectedPageIds.push(existingPage.id)
            console.log(`[StepConnectPages] Auto-selected existing page: ${existingPage.name}`)
          }
        }
        
        return false // Not added (already exists)
      }
      
      // Page doesn't exist, add it
      console.log(`[StepConnectPages] Adding new page to ${network}: ${page.name} (${page.network_id})`)
      
      // Safety check: ensure page has an ID (can be network_id as fallback)
      if (!page.id && page.network_id) {
        console.warn(`[StepConnectPages] Page has no UUID, using network_id as fallback: ${page.network_id}`)
        page.id = page.network_id
      }
      
      this.$store.commit('onboarding/ADD_CONNECTED_PAGE', {
        network: network,
        page: page
      })
      
      // Auto-select if under limit (only for current network)
      if (page.id && network === this.currentNetwork && this.selectedPageIds.length < this.maxSelectedPages) {
        this.selectedPageIds.push(page.id)
        console.log(`[StepConnectPages] Auto-selected new page: ${page.name}`)
      }
      
      return true // Added
    },

    skipStep() {
      console.log('[StepConnectPages] skipStep - checking for next network')
      console.log('[StepConnectPages] Current network index:', this.currentNetworkIndex)
      console.log('[StepConnectPages] Total networks:', this.selectedNetworks.length)
      
      // Check if there are more networks to connect
      if (this.currentNetworkIndex < this.selectedNetworks.length - 1) {
        console.log('[StepConnectPages] Moving to next network')
        
        // Save current selections before moving
        this.saveSelectedPages()
        
        // Move to next network
        this.currentNetworkIndex++
        this.selectedPageIds = []
        
        // Load selected pages for new network
        this.loadSelectedPages()
        
        this.notify({
          time: 3000,
          title: this.$t('onboarding.notifications.info_title'),
          text: this.$t('onboarding.connect_pages.info_network_skipped', { network: this.selectedNetworks[this.currentNetworkIndex - 1] }),
          color: 'primary'
        })
      } else {
        // No more networks - show exit confirmation modal
        console.log('[StepConnectPages] No more networks - showing exit confirmation')
        
        EventBus.$emit('showModal',
          this.$t('onboarding.modale.exit.title'),
          this.$t('onboarding.modale.exit.message'),
          this.$t('onboarding.modale.exit.confirm'),
          this.$t('onboarding.modale.exit.cancel'),
          this.confirmExitOnboarding,
          null
        )
      }
    },
    
    confirmExitOnboarding() {
      console.log('[StepConnectPages] Exit onboarding confirmed')
      // Reset all onboarding store state (also sets isOnboarding = false)
      this.$store.commit('onboarding/RESET_ONBOARDING')
      // Mark onboarding as skipped to prevent redirect loop
      localStorage.setItem('onboardingSkipped', 'true')
      // Clean up onboarding flags
      this.clearOnboardingFlags()
      // Redirect to dashboard
      this.$router.push({ name: 'dash' })
    },

    clearOnboardingFlags() {
      clearOnboardingFlags()
    },

    async goToNextStep() {
      // Save selected pages before navigating
      this.saveSelectedPages()
      this.persistOnboardingState() // CRITICAL: Persist before navigation
      
      // NOTE: We don't disconnect unselected pages here to allow users to go back and select forgotten pages
      // Disconnection will happen at the end of onboarding in StepSummary
      
      // Check if the network requires web extension (LinkedIn, TikTok)
      if (this.requiresExtension && this.selectedPageIds.length > 0) {
        this.$router.push({
          name: 'onboarding-connectExtensions',
          params: { network: this.currentNetwork }
        })
      }
      // Check if the network requires a native connection (Instagram)
      else if (this.requiresNativeConnection && this.selectedPageIds.length > 0) {
        this.$router.push({
          name: 'onboarding-connectNatives',
          params: { network: this.currentNetwork }
        })
      } else if (this.currentNetworkIndex < this.selectedNetworks.length - 1) {
        this.currentNetworkIndex++
        this.selectedPageIds = []
      } else {
        console.log('[StepConnectPages] All networks completed - redirecting to summary')
        this.$router.push({ name: 'onboarding-summary' })
      }
    },

    async disconnectUnselectedPages() {
      console.log('[StepConnectPages] Checking for unselected pages to disconnect...')
      
      const currentPages = this.connectedNetworkPages
      const selectedIds = this.selectedPageIds
      const preExistingIds = this.$store.getters['onboarding/getPreExistingPageIds'](this.currentNetwork)
      const newlyConnectedIds = this.$store.getters['onboarding/getNewlyConnectedPageIds'](this.currentNetwork)
      
      console.log('[StepConnectPages] Current pages:', currentPages.map(p => p.name))
      console.log('[StepConnectPages] Selected IDs:', selectedIds)
      console.log('[StepConnectPages] Pre-existing IDs:', preExistingIds)
      console.log('[StepConnectPages] Newly connected IDs:', newlyConnectedIds)
      
      // Find pages to disconnect: newly connected AND not selected
      const pagesToDisconnect = currentPages.filter(page => {
        const isNewlyConnected = newlyConnectedIds.includes(page.id)
        const isNotSelected = !selectedIds.includes(page.id)
        const shouldDisconnect = isNewlyConnected && isNotSelected
        
        if (shouldDisconnect) {
          console.log(`[StepConnectPages] Will disconnect: ${page.name} (newly connected + not selected)`)
        }
        
        return shouldDisconnect
      })
      
      if (pagesToDisconnect.length === 0) {
        console.log('[StepConnectPages] No pages to disconnect')
        return
      }
      
      console.log(`[StepConnectPages] Disconnecting ${pagesToDisconnect.length} unselected pages...`)
      
      // Disconnect pages from backend
      for (const page of pagesToDisconnect) {
        console.log(`[StepConnectPages] Disconnecting page: ${page.name} (${page.id})`)
        
        try {
          // Call deletePage API (from Pages mixin)
          await this.deletePage(page.id)
          
          // Remove from store
          this.$store.commit('onboarding/REMOVE_CONNECTED_PAGE', {
            network: this.currentNetwork,
            pageId: page.id
          })
          
          console.log(`[StepConnectPages] Successfully disconnected: ${page.name}`)
        } catch (error) {
          console.error(`[StepConnectPages] Error disconnecting ${page.name}:`, error)
        }
      }
      
      console.log('[StepConnectPages] Finished disconnecting unselected pages')
    },

    handleNetworkSelected(network) {
      // Save current network's selected pages before switching
      this.saveSelectedPages()
      
      // Change network index (watcher will automatically load selected pages)
      const index = this.selectedNetworks.indexOf(network)
      if (index !== -1) {
        this.currentNetworkIndex = index
        // Note: loadSelectedPages() will be called automatically by the currentNetwork watcher
      }
    },

    loadSelectedPages() {
      const savedPageIds = this.$store.state.onboarding.selectedPageIds[this.currentNetwork]
      console.log('[StepConnectPages] Loading selected pages for', this.currentNetwork, ':', savedPageIds)
      if (savedPageIds && Array.isArray(savedPageIds)) {
        this.selectedPageIds = [...savedPageIds]
      } else {
        this.selectedPageIds = []
      }
    },

    async loadExistingPages() {
      console.log('[StepConnectPages] Loading existing pages from Dashboard...')
      
      try {
        const response = await this.getPages()
        
        if (response.ok && response.body && response.body.pages) {
          const allPages = response.body.pages
          console.log('[StepConnectPages] Total pages from backend:', allPages.length)
          
          // Filter and add pages only for selected networks
          this.selectedNetworks.forEach(network => {
            const networkPages = allPages.filter(p => p.network === network)
            console.log(`[StepConnectPages] Found ${networkPages.length} existing pages for ${network}`)
            
            // Track pre-existing page IDs (pages that existed BEFORE onboarding)
            const preExistingIds = []
            
            networkPages.forEach(page => {
              // Check if page is not already in store
              const existingPages = this.connectedPages[network] || []
              const alreadyExists = existingPages.some(p => p.id === page.id)
              
              if (!alreadyExists) {
                console.log(`[StepConnectPages] Adding existing page to store: ${page.name} (${page.id})`)
                
                // Check if page has native connection
                const hasNativeConnection = page.story_config && page.story_config.created === true
                if (hasNativeConnection) {
                  console.log(`[StepConnectPages] Page ${page.name} already has native connection (story_config.created = true)`)
                }
                
                // Mark as pre-existing (not newly connected during onboarding)
                preExistingIds.push(page.id)
                
                // Use addPageIfNotExists to prevent duplicates
                const wasAdded = this.addPageIfNotExists(network, {
                  id: page.id,
                  network_id: page.network_id,
                  name: page.name,
                  username: page.username || page.name,
                  avatar: page.avatar,
                  story_config: page.story_config || null, // Include story_config from dashboard
                  hasUuid: true,
                  preExisting: true // Flag to indicate this page existed before onboarding
                })
                
                // If page was newly added, ensure it's selected by default
                if (wasAdded) {
                  console.log(`[StepConnectPages] Auto-selected existing page: ${page.name}`)
                }
              } else {
                // Page already in store - mark as pre-existing anyway
                preExistingIds.push(page.id)
              }
            })
            
            // Mark all these pages as pre-existing in the store
            if (preExistingIds.length > 0) {
              this.$store.commit('onboarding/SET_PRE_EXISTING_PAGE_IDS', {
                network: network,
                pageIds: preExistingIds
              })
              
              // Merge pre-existing IDs into selectedPageIds WITHOUT overwriting
              // existing selections (pages connected during onboarding)
              const currentSelected = this.$store.state.onboarding.selectedPageIds[network] || []
              const merged = [...new Set([...currentSelected, ...preExistingIds])]
              this.$store.commit('onboarding/SET_SELECTED_PAGE_IDS', {
                network: network,
                pageIds: merged
              })
              
              console.log(`[StepConnectPages] Merged ${preExistingIds.length} pre-existing + ${currentSelected.length} existing selections = ${merged.length} total for ${network}`)
            }
          })
          
          console.log('[StepConnectPages] Finished loading existing pages')
          console.log('[StepConnectPages] Pre-existing pages:', this.$store.state.onboarding.preExistingPageIds)
          console.log('[StepConnectPages] Auto-selected pages:', this.$store.state.onboarding.selectedPageIds)
        }
      } catch (error) {
        console.error('[StepConnectPages] Error loading existing pages:', error)
      }
    },

    restoreOnboardingState() {
      // Restore state after an OAuth return
      const isOnboardingMode = localStorage.getItem('onboarding_mode')
      const savedNetwork = localStorage.getItem('onboarding_current_network')
      const savedIndex = localStorage.getItem('onboarding_current_index')
      const savedNetworks = localStorage.getItem('onboarding_selected_networks')
      const savedFeatures = localStorage.getItem('onboarding_selected_features')
      
      console.log('[StepConnectPages] localStorage check:', {
        onboarding_mode: isOnboardingMode,
        saved_network: savedNetwork,
        saved_index: savedIndex,
        saved_networks: savedNetworks,
        saved_features: savedFeatures
      })
      
      if (isOnboardingMode === 'true') {
        // Restore Vuex store data that was lost on page reload
        if (savedNetworks) {
          const networks = JSON.parse(savedNetworks)
          console.log('[StepConnectPages] Restoring selectedNetworks to store:', networks)
          this.$store.commit('onboarding/SET_NETWORKS', networks)
        }
        
        if (savedFeatures) {
          const features = JSON.parse(savedFeatures)
          console.log('[StepConnectPages] Restoring selectedFeatures to store:', features)
          this.$store.commit('onboarding/SET_FEATURES', features)
        }
        
        // Restore page connection data (connectedPages, selectedPageIds, preExistingPageIds, etc.)
        const pagesRestored = restoreOnboardingPages(this.$store)
        console.log('[StepConnectPages] Page connection data restored:', pagesRestored)
        
        // Wait for next tick to ensure computed properties are updated
        this.$nextTick(() => {
          // Restore network index if saved, BUT ONLY if we didn't receive an explicit network or networkIndex from route params
          // (to avoid overwriting the correct navigation from StepConnectExtensions/StepConnectNatives/Summary)
          const hasExplicitNetworkIndex = this.$route.params.networkIndex !== undefined
          const hasExplicitNetwork = !!this.$route.params.network
          if (savedIndex && !hasExplicitNetworkIndex && !hasExplicitNetwork) {
            this.currentNetworkIndex = parseInt(savedIndex)
            console.log('[StepConnectPages] Restored to network:', this.selectedNetworks[this.currentNetworkIndex])
          } else if (hasExplicitNetworkIndex) {
            console.log('[StepConnectPages] Skipping localStorage restore - using explicit networkIndex from route')
          }
          
          // Refresh pre-existing pages from backend to ensure we have fresh data
          // This is critical after OAuth redirect since restored pages from localStorage may be stale
          this.loadExistingPages().then(() => {
            this.loadSelectedPages()
            this.handleOAuthRedirects()
          })
        })
        
        // DON'T clear flags here - they must persist for multiple OAuth connections
        // Flags will be cleared when user exits onboarding (via Terminer or back to dashboard)
        console.log('[StepConnectPages] \u2705 State restored from localStorage, keeping flags for next OAuth if needed')
      }
    },

    handleOAuthRedirects() {
      if (this.oauthCallbackProcessed) return
      
      const hashParts = location.hash.split('?')
      const queryString = hashParts[1] || ''
      const params = new URLSearchParams(queryString)
      
      const oauth_token = params.get('oauth_token')
      const oauth_verifier = params.get('oauth_verifier')
      const code = params.get('code')
      const state = params.get('state')

      const showSuccess = (message) => {
        this.notify({
          time: 5000,
          title: this.$t('onboarding.notifications.success_title', 'Succès'),
          text: message || this.$t('onboarding.connect_pages.oauth_success_default', 'Connexion réussie'),
          color: 'success',
          id: 'norepeat'
        })
      }

      const showFailed = (error) => {
        let title = this.$t('onboarding.notifications.error_title', 'Erreur')
        let text = error || this.$t('onboarding.connect_pages.oauth_error_default', 'Erreur de connexion')

        // If error is a backend error key (e.g. "dash.error.page_already_exist_other_account"),
        // use its .title / .body translation keys instead of displaying the raw key string.
        if (error && this.$te(`${error}.title`) && this.$te(`${error}.body`)) {
          title = this.$t(`${error}.title`)
          text = this.$t(`${error}.body`)
        }

        this.notify({
          time: 10000,
          title,
          text,
          color: 'danger',
          id: 'norepeat'
        })
      }

      // Twitter OAuth 1.0 callback
      if (oauth_token && oauth_verifier) {
        this.handleOAuth1Callback({ oauth_token, oauth_verifier, showSuccess, showFailed })
        return
      }
      
      // OAuth 2.0 callback
      if (code) {
        this.handleOAuth2Callback({ code, state, showSuccess, showFailed })
        return
      }
      
      this.oauthCallbackProcessed = true
    },

    // Generic OAuth 1.0 handler
    handleOAuth1Callback({ oauth_token, oauth_verifier, showSuccess, showFailed }) {
      console.log('[StepConnectPages] Processing OAuth 1.0 callback (Twitter)')
      
      // Activate loading spinner on OAuth return
      this.isConnecting = true
      
      this.addTwitterV1({
        oauth_token,
        oauth_verifier,
        refresh_page: null
      })
      .then((res) => {
        console.log('[StepConnectPages] Twitter API response:', res)
        const hasValidUuid = res && res.ok && res.body && res.body.page && res.body.page.id
        
        if (hasValidUuid) {
          this.addPageIfNotExists('x', {
            id: res.body.page.id,
            network_id: res.body.page.network_id,
            name: res.body.page.name,
            username: res.body.page.name,
            avatar: res.body.page.avatar,
            hasUuid: true
          })
          
          this.$nextTick(() => {
            this.$forceUpdate()
          })
        } else {
          console.warn('[StepConnectPages] Backend returned invalid response for Twitter, using fallback')
          console.error('[StepConnectPages] Response details:', {
            ok: res && res.ok,
            status: res && res.status,
            hasBody: res && !!res.body,
            hasPage: res && res.body && !!res.body.page
          })
          
          // Fallback: Try to extract network_id from response or use screen_name
          const fallbackId = (res && res.body && res.body.page && res.body.page.network_id) || 'twitter_user'
          const fallbackName = (res && res.body && res.body.page && res.body.page.name) || 'Twitter Account'
          
          this.addPageIfNotExists('x', {
            id: fallbackId,
            network_id: fallbackId,
            name: fallbackName,
            username: fallbackName,
            avatar: (res && res.body && res.body.page && res.body.page.avatar) || null,
            hasUuid: false
          })
          
          this.$nextTick(() => {
            this.$forceUpdate()
          })
        }
        
        history.replaceState({}, document.title, location.pathname + location.hash)
        this.saveSelectedPages()
        this.finalizeConnection(this.$t(this.oauthProviders.x.successMessage))
        this.oauthCallbackProcessed = true
      })
      .catch(e => {
        console.error('[StepConnectPages] Twitter connection error:', e)
        showFailed(e.body?.error || this.$t('onboarding.connect_pages.error_twitter_connection'))
        this.isConnecting = false
        this.oauthCallbackProcessed = true
      })
    },

    // Generic OAuth 2.0 handler with provider detection
    handleOAuth2Callback({ code, state, showSuccess, showFailed }) {
      this.isConnecting = true
      
      const provider = this.detectOAuth2Provider(state)
      
      if (!provider) {
        showFailed(this.$t('onboarding.connect_pages.error_oauth_provider_unknown'))
        this.isConnecting = false
        return
      }
      
      const verifyParams = provider.requiresState ? { code, state } : { code }
      let promiseChain
      
      // Special handling for Twitter OAuth 2.0 which uses newPage directly
      if (provider.usesNewPageDirectly) {
        console.log('[StepConnectPages] Twitter OAuth 2.0 - calling newPage directly with code')
        promiseChain = this.newPage({
          network: 'twitter',
          code: code
        }).then((newPageRes) => {
          console.log('[StepConnectPages] Twitter newPage response:', newPageRes)
          
          // Check if response is successful - backend returns { success: true, page: {...} }
          if (newPageRes && newPageRes.ok && newPageRes.body && newPageRes.body.success) {
            console.log('[StepConnectPages] Twitter page created successfully')
            // After newPage, fetch all pages to get the Twitter account
            console.log('[StepConnectPages] Fetching pages after Twitter connection')
            return this.getPages()
          } else {
            console.error('[StepConnectPages] Invalid Twitter newPage response:', newPageRes)
            console.error('[StepConnectPages] Response details:', {
              ok: newPageRes && newPageRes.ok,
              status: newPageRes && newPageRes.status,
              success: newPageRes && newPageRes.body && newPageRes.body.success
            })
            throw new Error(this.$t('onboarding.connect_pages.error_twitter_create_failed'))
          }
        }).then(pagesRes => {
          console.log('[StepConnectPages] getPages response:', pagesRes)
          const allPages = pagesRes.body.pages || []
          const twitterPages = allPages.filter(p => p.network === 'twitter')
          
          console.log('[StepConnectPages] Found', twitterPages.length, 'Twitter accounts')
          
          if (twitterPages.length > 0) {
            return {
              body: {
                pages: twitterPages
              }
            }
          } else {
            throw new Error(this.$t('onboarding.connect_pages.error_twitter_not_found'))
          }
        })
      } else {
        promiseChain = this[provider.verifyMethod](verifyParams)
      }
      
      // For LinkedIn, add an extra step to fetch organizations
      if (provider.networkKey === 'linkedin') {
        promiseChain = promiseChain
          .then(() => {
            console.log('[StepConnectPages] LinkedIn verified, fetching organizations...')
            return this.getLinkedInOrganizations()
          })
          .then(({ body }) => {
            console.log('[StepConnectPages] LinkedIn organizations response:', body)
            const { elements = [] } = body
            
            // Transform LinkedIn data to standard format
            const linkedinPages = elements.map(e => ({
              network_id: e['organizationalTarget~'].id,
              name: e['organizationalTarget~'].localizedName,
              avatar: e['organizationalTarget~'].logoV2?.['original~']?.elements?.[1]?.identifiers?.[0]?.identifier || null,
              username: e['organizationalTarget~'].localizedName // Use name as username
            }))
            
            console.log('[StepConnectPages] Transformed LinkedIn pages:', linkedinPages)
            
            // Save each LinkedIn page to backend using newPage()
            const savePromises = linkedinPages.map(page => {
              console.log('[StepConnectPages] Saving LinkedIn page to backend:', page.name)
              return this.newPage({
                network: 'linkedin',
                network_id: page.network_id,
                name: page.name,
                avatar: page.avatar
              }).catch(err => {
                console.error('[StepConnectPages] Error saving LinkedIn page:', page.name, err)
                // Continue even if one page fails
                return null
              })
            })
            
            // Wait for all pages to be saved, then return them
            return Promise.all(savePromises).then(() => {
              console.log('[StepConnectPages] All LinkedIn pages saved to backend')
              return {
                body: {
                  pages: linkedinPages
                }
              }
            })
          })
      }
      
      // For Pinterest, user account becomes the "page"
      if (provider.networkKey === 'pinterest') {
        promiseChain = promiseChain
          .then((res) => {
            console.log('[StepConnectPages] Pinterest verified, creating page from user account...')
            console.log('[StepConnectPages] Pinterest response:', res.body)
            
            const { user, token: oauth_token } = res.body
            
            // Build token object as expected by backend
            const token = {
              content: oauth_token.access_token,
              network: 'pinterest',
              expires_at: oauth_token.expires_in,
              network_id: user.username,
              refresh_token: oauth_token.refresh_token,
              refresh_token_expires_at: oauth_token.refresh_token_expires_in
            }
            
            console.log('[StepConnectPages] Saving Pinterest account as page:', user.username)
            
            // Save the Pinterest user account as a "page"
            return this.newPage({
              network: 'pinterest',
              token,
              name: user.username,
              avatar: user.profile_image || null,
              network_id: user.username
            }).then((pinterestRes) => {
              console.log('[StepConnectPages] Pinterest newPage response:', pinterestRes)
              
              // Check if response is successful - backend returns { success: true, page: {id: "..."} }
              // IMPORTANT: Check page.id exists, not just page object (backend can return empty {})
              const hasValidUuid = pinterestRes && pinterestRes.ok && pinterestRes.body && pinterestRes.body.success && pinterestRes.body.page && pinterestRes.body.page.id
              
              if (hasValidUuid) {
                console.log('[StepConnectPages] Pinterest account saved to backend with UUID:', pinterestRes.body.page.id)
                
                // Return formatted response with the created page (includes UUID)
                return {
                  body: {
                    pages: [{
                      id: pinterestRes.body.page.id,
                      network_id: user.username,
                      name: user.username,
                      avatar: user.profile_image || null,
                      username: user.username,
                      hasUuid: true
                    }]
                  }
                }
              } else {
                console.warn('[StepConnectPages] Backend returned invalid response for Pinterest, using fallback')
                console.error('[StepConnectPages] Response details:', {
                  ok: pinterestRes && pinterestRes.ok,
                  status: pinterestRes && pinterestRes.status,
                  success: pinterestRes && pinterestRes.body && pinterestRes.body.success,
                  hasPage: pinterestRes && pinterestRes.body && !!pinterestRes.body.page
                })
                
                // Return fallback with network_id as id
                return {
                  body: {
                    pages: [{
                      id: user.username,
                      network_id: user.username,
                      name: user.username,
                      avatar: user.profile_image || null,
                      username: user.username,
                      hasUuid: false
                    }]
                  }
                }
              }
            }).catch(err => {
              console.error('[StepConnectPages] Error saving Pinterest account:', err)
              throw new Error(this.$t('onboarding.connect_pages.error_pinterest_save_failed'))
            })
          })
      }
      
      promiseChain
        .then(res => {
          console.log(`[StepConnectPages] ${provider.name} API response:`, res)
          console.log(`[StepConnectPages] ${provider.name} res.body:`, res.body)
          console.log(`[StepConnectPages] ${provider.name} res.body.pages:`, res.body?.pages)
          
          // Special handling for YouTube and TikTok - backend creates resources but doesn't return them
          if (provider.networkKey === 'youtube') {
            console.log('[StepConnectPages] YouTube special handling - fetching channels via getPages()')
            
            // YouTube backend creates the channels, now we need to fetch all pages to get them
            return this.getPages().then(pagesRes => {
              console.log('[StepConnectPages] getPages response:', pagesRes)
              const allPages = pagesRes.body.pages || []
              const youtubeChannels = allPages.filter(p => p.network === 'youtube')
              
              console.log('[StepConnectPages] Found', youtubeChannels.length, 'YouTube channels')
              
              if (youtubeChannels.length > 0) {
                // Transform to expected format
                return {
                  body: {
                    pages: youtubeChannels
                  }
                }
              } else {
                throw new Error(this.$t('onboarding.connect_pages.error_youtube_not_found'))
              }
            })
          }
          
          if (provider.networkKey === 'tiktok') {
            console.log('[StepConnectPages] TikTok special handling - fetching accounts via getPages()')
            
            // TikTok backend creates the account, now we need to fetch all pages to get them
            return this.getPages().then(pagesRes => {
              console.log('[StepConnectPages] getPages response:', pagesRes)
              const allPages = pagesRes.body.pages || []
              const tiktokAccounts = allPages.filter(p => p.network === 'tiktok')
              
              console.log('[StepConnectPages] Found', tiktokAccounts.length, 'TikTok accounts')
              
              if (tiktokAccounts.length > 0) {
                // Transform to expected format
                return {
                  body: {
                    pages: tiktokAccounts
                  }
                }
              } else {
              throw new Error(this.$t('onboarding.connect_pages.error_tiktok_not_found'))
              }
            })
          }
          
          if (provider.networkKey === 'threads') {
            console.log('[StepConnectPages] Threads special handling - fetching accounts via getPages()')
            
            // Threads backend creates the account, now we need to fetch all pages to get them
            return this.getPages().then(pagesRes => {
              console.log('[StepConnectPages] getPages response:', pagesRes)
              const allPages = pagesRes.body.pages || []
              const threadsAccounts = allPages.filter(p => p.network === 'threads')
              
              console.log('[StepConnectPages] Found', threadsAccounts.length, 'Threads accounts')
              
              if (threadsAccounts.length > 0) {
                // Transform to expected format
                return {
                  body: {
                    pages: threadsAccounts
                  }
                }
              } else {
                throw new Error(this.$t('onboarding.connect_pages.error_threads_not_found'))
              }
            })
          }
          
          return res
        })
        .then(res => {
          if (res.body && res.body.pages && res.body.pages.length > 0) {
            console.log(`[StepConnectPages] Found ${res.body.pages.length} ${provider.name} pages`)
            
            res.body.pages.forEach((page, index) => {
              console.log(`[StepConnectPages] Processing ${provider.name} page ${index + 1}:`, page)
              console.log(`[StepConnectPages] Page structure:`, {
                network_id: page.network_id,
                name: page.name,
                avatar: page.avatar,
                full_page: page
              })
              
              const pageToAdd = {
                id: page.id,
                network_id: page.network_id,
                name: page.name,
                username: page.username || page.name || page.network_id, // Use real username from backend
                avatar: page.avatar
              }
              
              console.log(`[StepConnectPages] Adding to store with networkKey='${provider.networkKey}':`, pageToAdd)
              console.log(`[StepConnectPages] Store BEFORE commit:`, JSON.parse(JSON.stringify(this.$store.state.onboarding.connectedPages)))
              
              this.addPageIfNotExists(provider.networkKey, pageToAdd)
              
              console.log(`[StepConnectPages] Store AFTER commit:`, JSON.parse(JSON.stringify(this.$store.state.onboarding.connectedPages)))
              console.log(`[StepConnectPages] connectedPages[${provider.networkKey}]:`, this.$store.state.onboarding.connectedPages[provider.networkKey])
            })
            
            console.log(`[StepConnectPages] Before $nextTick - currentNetwork:`, this.currentNetwork)
            console.log(`[StepConnectPages] Before $nextTick - connectedNetworkPages:`, this.connectedNetworkPages)
            
            // Force Vue to update
            this.$nextTick(() => {
              console.log('[StepConnectPages] After $nextTick - currentNetwork:', this.currentNetwork)
              console.log('[StepConnectPages] After $nextTick - connectedNetworkPages:', this.connectedNetworkPages)
              console.log('[StepConnectPages] After $nextTick - store connectedPages:', this.$store.state.onboarding.connectedPages)
              this.$forceUpdate()
              
              // Double check after forceUpdate
              this.$nextTick(() => {
                console.log('[StepConnectPages] After $forceUpdate - connectedNetworkPages:', this.connectedNetworkPages)
              })
            })
            
            history.replaceState({}, document.title, location.pathname + location.hash)
            this.saveSelectedPages()
            this.persistOnboardingState() // CRITICAL: Persist to localStorage after OAuth
            this.finalizeConnection(this.$t(provider.successMessage))
            this.oauthCallbackProcessed = true
          } else {
            console.log(`[StepConnectPages] No pages in ${provider.name} response`)
            console.log(`[StepConnectPages] Full response body:`, JSON.stringify(res.body, null, 2))
            console.warn(`[StepConnectPages] Backend did not return pages array for ${provider.name}`)
            console.warn('[StepConnectPages] This might indicate:')
            console.warn('  1. User has no organization pages with admin access')
            console.warn('  2. Backend failed to fetch organization pages from LinkedIn API')
            console.warn('  3. LinkedIn app scopes are insufficient or not approved')
            history.replaceState({}, document.title, location.pathname + location.hash)
            
            // More helpful error message for LinkedIn
            const errorMessage = provider.networkKey === 'linkedin'
              ? this.$t('onboarding.connect_pages.error_linkedin_no_page')
              : this.$t('onboarding.connect_pages.error_no_pages_found', { provider: provider.name })
            
            showFailed(errorMessage)
            this.isConnecting = false
            this.oauthCallbackProcessed = true
          }
        })
        .catch(e => {
          console.error(`[StepConnectPages] ${provider.name} connection error:`, e)
          console.error(`[StepConnectPages] ${provider.name} error details:`, e.body)
          showFailed(e.body?.error || this.$t('onboarding.connect_pages.error_connection_failed', { provider: provider.name }))
          this.isConnecting = false
          this.oauthCallbackProcessed = true
        })
    },

    // Detect OAuth 2.0 provider based on currentNetwork or state
    detectOAuth2Provider(state) {
      // Priority 1: Check by state parameter (Threads, Instagram Business)
      for (const [key, config] of Object.entries(this.oauthProviders)) {
        if (config.detectBy === 'state' && config.stateValue === state) {
          return {
            name: key.charAt(0).toUpperCase() + key.slice(1),
            networkKey: key,
            ...config
          }
        }
      }
      
      // Priority 2: Check by currentNetwork (restored from localStorage)
      // Fallback to localStorage if Vuex store lost the network during redirect
      const resolvedNetwork = this.currentNetwork || localStorage.getItem('onboarding_current_network')
      
      if (resolvedNetwork && this.oauthProviders[resolvedNetwork]) {
        const config = this.oauthProviders[resolvedNetwork]
        if (config.detectBy === 'currentNetwork') {
          console.log('[StepConnectPages] Detected OAuth provider by currentNetwork:', resolvedNetwork,
            resolvedNetwork !== this.currentNetwork ? '(from localStorage fallback)' : '')
          return {
            name: resolvedNetwork.charAt(0).toUpperCase() + resolvedNetwork.slice(1),
            networkKey: resolvedNetwork,
            ...config
          }
        }
      }
      
      console.warn('[StepConnectPages] Could not detect OAuth 2.0 provider. State:', state, 'currentNetwork:', this.currentNetwork, 'localStorage fallback:', localStorage.getItem('onboarding_current_network'))
      return null
    }
  },
  async mounted() {
    console.log('=== [StepConnectPages] MOUNTED ===')
    
    // HANDLE OAUTH PARAMS BEFORE HASH (LinkedIn, Facebook sometimes redirect like: ?code=XXX#/path)
    const searchParams = new URLSearchParams(window.location.search)
    const hasOAuthParamsBeforeHash = searchParams.has('code') || searchParams.has('oauth_token')
    
    if (hasOAuthParamsBeforeHash && !window.location.hash.includes('?')) {
      console.log('[StepConnectPages] OAuth params detected BEFORE hash - moving them after hash')
      console.log('[StepConnectPages] Current URL:', window.location.href)
      
      // Move params from ?code=XXX#/path to #/path?code=XXX
      const currentHash = window.location.hash.replace('#', '')
      const newUrl = `${window.location.origin}${window.location.pathname}#${currentHash}${window.location.search}`
      
      console.log('[StepConnectPages] Redirecting to:', newUrl)
      window.location.href = newUrl
      return
    }
    
    console.log('[StepConnectPages] selectedNetworks:', this.selectedNetworks)
    console.log('[StepConnectPages] currentNetwork:', this.currentNetwork)
    console.log('[StepConnectPages] currentNetworkName:', this.currentNetworkName)
    console.log('[StepConnectPages] Store state:', {
      connectedPages: this.$store.state.onboarding.connectedPages,
      selectedPageIds: this.$store.state.onboarding.selectedPageIds,
      selectedFeatures: this.$store.state.onboarding.selectedFeatures
    })
    console.log('[StepConnectPages] LocalStorage:', {
      onboarding_mode: localStorage.getItem('onboarding_mode'),
      current_network: localStorage.getItem('onboarding_current_network'),
      current_index: localStorage.getItem('onboarding_current_index')
    })
    console.log('[StepConnectPages] URL params:', {
      search: location.search,
      hash: location.hash,
      href: location.href
    })

    if (this.selectedNetworks.length === 0) {
      // Check if we're in an OAuth return (onboarding_mode flag present)
      const isOAuthReturn = localStorage.getItem('onboarding_mode') === 'true'
      
      if (isOAuthReturn) {
        console.log('[StepConnectPages] No networks but onboarding_mode detected - OAuth return, restoring state first')
        // Don't redirect yet, let restoreOnboardingState() handle it
      } else {
        // Check if we're in a plain page reload (parent Onboarding.vue will restore Vuex shortly
        // in its own mounted(), which fires after child components due to Vue 2 lifecycle order)
        const hasSavedNetworks = (() => {
          try {
            const str = localStorage.getItem('onboarding_state')
            return str ? ((JSON.parse(str).selectedNetworks?.length || 0) > 0) : false
          } catch (e) {
            console.error('[StepConnectPages] Failed to parse onboarding_state:', e)
            return false
          }
        })()

        if (hasSavedNetworks) {
          console.log('[StepConnectPages] Saved state found - deferring init to selectedNetworks watcher (reload scenario)')
          this._deferredInit = true
          return
        }

        console.log('[StepConnectPages] No networks selected, redirecting to networks step')
        this.$router.push({ name: 'onboarding-networks' })
        return
      }
    }
    
    // Check if returning from native connections (via route params)
    if (this.$route.params.fromNatives === true) {
      console.log('[StepConnectPages] Returning from native connections')
      console.log('[StepConnectPages] Current network index:', this.currentNetworkIndex)
      
      // Move to next network
      if (this.currentNetworkIndex < this.selectedNetworks.length - 1) {
        this.currentNetworkIndex++
        this.selectedPageIds = []
        console.log('[StepConnectPages] Moved to next network:', this.selectedNetworks[this.currentNetworkIndex])
      } else {
        console.log('[StepConnectPages] All networks completed, redirecting to summary')
        this.$router.push({ name: 'onboarding-summary' })
        return
      }
    }
    
    // Check if coming from StepConnectNatives or StepConnectExtensions with specific network index
    const hasExplicitNetworkIndex = this.$route.params.networkIndex !== undefined
    if (hasExplicitNetworkIndex) {
      const networkIndex = parseInt(this.$route.params.networkIndex)
      console.log('[StepConnectPages] Coming from StepConnectNatives/Extensions with network index:', networkIndex)
      
      if (networkIndex >= 0 && networkIndex < this.selectedNetworks.length) {
        this.currentNetworkIndex = networkIndex
        this.selectedPageIds = []
        console.log('[StepConnectPages] Set network index to:', networkIndex, '- Network:', this.selectedNetworks[networkIndex])
      } else {
        console.warn('[StepConnectPages] Invalid network index:', networkIndex)
      }
    }
    
    // Check if coming from summary with specific network
    if (this.$route.params.network) {
      const targetNetwork = this.$route.params.network
      console.log('[StepConnectPages] Coming from summary for network:', targetNetwork)
      
      const networkIndex = this.selectedNetworks.indexOf(targetNetwork)
      if (networkIndex !== -1) {
        this.currentNetworkIndex = networkIndex
        console.log('[StepConnectPages] Set network index to:', networkIndex)
      } else {
        console.warn('[StepConnectPages] Network not found in selected networks:', targetNetwork)
      }
    }
    
    // Restore state after OAuth redirect
    this.restoreOnboardingState()
    
    // Load existing pages from Dashboard for selected networks
    if (localStorage.getItem('onboarding_mode') !== 'true') {
      await this.loadExistingPages()
    }
    
    // Load selected pages for current network (only if not in OAuth return mode)
    if (localStorage.getItem('onboarding_mode') !== 'true') {
      this.loadSelectedPages()
      
      // OAuth redirects are handled in restoreOnboardingState() after state restoration
      // No need to call handleOAuthRedirects() here to avoid duplicate processing
      console.log('[StepConnectPages] Not in OAuth return mode - state loaded')
    }
  },

  beforeDestroy() {
    // Clean up any OAuth parameters
    const params = new URLSearchParams(location.search)
    if (params.get('code') || params.get('oauth_token') || params.get('state')) {
      history.replaceState({}, document.title, location.pathname + location.hash)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../variables.scss';

.onboarding-connect-pages {
  display: flex;
  min-height: 100vh;
}

.onboarding-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 64px;
  overflow-y: auto;
  overflow-x: hidden;

  &::after {
    content: '';
    display: block;
    height: 100px;
    flex-shrink: 0;
  }
}

/* Header section */
.onboarding-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  margin-bottom: 24px;

  .onboarding-network-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: $bg-ui-selected;
    padding: 12px;
  }

  .onboarding-header-content {
    flex: 1;
  }

  .onboarding-header-title {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px 0;
  }

  .onboarding-header-subtitle {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
    line-height: 1.6;

      strong {
        color: $primary-color;
        font-weight: 600;
      }
    }
}

.onboarding-features-badges {
  display: flex;
  flex-wrap: wrap;
  overflow: visible;
  justify-content: center;
  gap: 8px;
  margin-bottom: 32px;

  .onboarding-feature-badge {
    position: relative;
    padding: 6px 12px;
    background: $bg-ui-selected;
    color: $primary-color;
    font-size: 13px;
    font-weight: 500;
    border-radius: 16px;
    text-align: center;
    white-space: nowrap;
    transition: all 0.1s ease;

    &:hover {
      transform: translateY(-1px);
    }

    &[data-tooltip]:not([data-tooltip=""]) {
      cursor: default;

      &::before {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%) translateY(-4px);
        padding: 6px 10px;
        background: #424242;
        color: #fff;
        font-size: 12px;
        font-weight: 500;
        border-radius: 6px;
        white-space: normal;
        max-width: 300px;
        min-width: 120px;
        text-align: center;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.1s ease, transform 0.1s ease;
        z-index: 1000;
      }

      &::after {
        content: '';
        position: absolute;
        bottom: calc(100% - 2px);
        left: 50%;
        transform: translateX(-50%) translateY(-4px);
        border: 6px solid transparent;
        border-top-color: #424242;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.1s ease, transform 0.1s ease;
        z-index: 1000;
      }

      &:hover::before,
      &:hover::after {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }
  }
}

.onboarding-quota-warning {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 24px;
  max-width: 600px;
  color: #92400e;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    display: block;
    margin-bottom: 4px;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
  }

  a {
    color: #b45309;
    text-decoration: underline;
    font-weight: 500;

    &:hover {
      color: #92400e;
    }
  }
}

.onboarding-pages-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(280px, 1fr));
  gap: 16px;
  min-height: 80px;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 24px;
  max-width: 600px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;

    &:hover {
      background: #94a3b8;
    }
  }
}

.onboarding-page-card {
  position: relative;
  background: #ffffff;
  border: 1px solid $border-ui;
  border-radius: 12px;
  padding: 20px;
  // margin: 0 20px;

  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: $primary-color;
  }

  &.selected {
    border-color: $primary-color;
    background: $bg-ui-selected;

    .avatar-placeholder {
      background-color: $primary-color;
      color: $bg-ui-selected;
    }
  }

  &.pre-existing {
    border-color: #d1d5db;
    background: #fafafa;

    &:hover {
      border-color: #d1d5db;
      cursor: default;
    }

    &.selected {
      border-color: $primary-color;
      background: $bg-ui-selected;
      opacity: 0.9;
    }
  }

  &.non-deselectable {
    cursor: not-allowed;
    
    &:hover {
      border-color: $primary-color;
    }
  }

  &.onboarding-add-page-card {
    justify-content: center;
    border: 1px dashed #d1d5db;
    background: #f9fafb;

    &:hover:not(.disabled) {
      border-color: $primary-color;
      background: #ffffff;
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      
      .onboarding-add-icon {
        background: #e5e7eb;
        color: #9ca3af;
      }

      .onboarding-add-text {
        color: #9ca3af;
      }
    }

    .onboarding-add-icon {
      width: 30px;
      aspect-ratio: 1/1;
      border-radius: 50%;
      background: $bg-ui-selected;
      color: $primary-color;
      font-size: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .onboarding-add-text {
      font-size: 16px;
      color: $secondary-text-color;
    }
  }
}

.pre-existing-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 4px 8px;
  background: #e5d8e093;
  color: $primary-color;
  font-size: 10px;
  font-weight: 600;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  z-index: 1;
}

.onboarding-page-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.onboarding-page-info {
  flex: 1;
  min-width: 0; // Force flex item to respect text-overflow
  overflow: hidden; // Prevent content from overflowing

  .onboarding-page-name {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
    overflow: hidden; // Ensure text does not overflow
    text-overflow: ellipsis; // Add ellipsis for overflowed text
    white-space: nowrap; // Prevent text from wrapping
  }

  .onboarding-page-username {
    font-size: 13px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    // max-width: 180px;
  }
}

.onboarding-selection-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px; // Fixed width to prevent overflow
  height: 24px; // Fixed height to prevent overflow
}

.onboarding-selection-check {
  width: 24px;
  height: 24px;
  border: 1px solid $border-ui;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;

  &::before {
    content: '✓';
    display: none;
    color: white;
    font: bold 14px/1 sans-serif;
    font-weight: 500;
  }

  &.checked {
    background-color: $primary-color;
    border: none;
    border-color: $primary-color;

    &::before {
      display: block;
    }
  }
}

/* Footer section */
.onboarding-footer-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-left: 400px;
}

.onboarding-pages-counter {
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  margin-bottom: 60px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;

  & :nth-last-child(2) {
    margin-top: 8px;
  }
}

.onboarding-footer {
  display: flex;
  align-items: center;
  gap: 12px;

  .onboarding-btn-secondary,
  .onboarding-btn-primary {
    padding: 12px 24px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
  }

  .onboarding-btn-secondary {
    background: transparent;
    color: #6b7280;

    &:hover {
      color: #111827;
    }
  }

  .onboarding-btn-primary {
    background: $bg-ui-selected;
    color: $primary-color;
    display: flex;
    align-items: center;
    gap: 12px;

    &:hover:not(:disabled) {
      background: $primary-color;
      color: #ffffff;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .onboarding-step-indicator {
      font-size: 13px;
      opacity: 0.8;
    }
  }
}

.avatar-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: $bg-ui-selected;
  color: $primary-color;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  flex-shrink: 0;
}

@media (max-width: 1150px) {
  .onboarding-pages-list {
    grid-template-columns: repeat(2, minmax(200px, 1fr));
  }

  .onboarding-page-card {
    // margin-right: 0;
    margin: 0;
    &.onboarding-add-page-card {
      .onboarding-add-text{
        display: none;
      }
    }

    .onboarding-selection-indicator {
      display: none;
    }

    .pre-existing-badge {
      display: none;
      // TODO : add corresponding Icon
    }  
  }

  .onboarding-footer-container {
    transform: translate(130px);
    margin-left: 0;
    // flex-wrap: nowrap;
  }
}

@media (max-width: 800px) {

  .onboarding-features-badges
    .onboarding-feature-badge {
        padding: 6px 5px;
    }

  .onboarding-pages-list {
    grid-template-columns: repeat(1, minmax(200px, 1fr));
  }

  .onboarding-page-card {
    // margin-right: 0;
    margin: 0;
    &.onboarding-add-page-card {
      .onboarding-add-text{
        display: none;
      }
    }

    .onboarding-selection-indicator {
      display: none;
    }
  }
  
  .onboarding-pages-counter {
    transform: translate(0px);
  }

  .onboarding-footer-container {
    transform: translate(0px);
    
    .onboarding-step-indicator {
      display: none;
    }
  }

}
</style>
